import * as React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useRequest } from 'ahooks';
import { useDispatch, useSelector } from 'react-redux';
import { selectActiveProfile } from '@stores/auth/selectors';

import OktaContext from './OktaContext';
import PropTypes from 'prop-types';
import { logoutAction, setTokenLogin } from '@stores/auth/actions';
import { clearAllCookies } from '@helpers/cookie';
import { ConfirmationModal } from '@components/molecules/PopupModal/ConfirmationModal.jsx';
import { BASE_URL } from '../../env';

const Security = ({ oktaAuth, publicRoutes, children }) => {
  const [
    isOpenNotificationTokenIsClearDialog,
    setIsOpenNotificationTokenIsClearDialog,
  ] = React.useState(false);
  const [authState, setAuthState] = React.useState(() => {
    if (!oktaAuth) {
      return null;
    }
    return oktaAuth.authStateManager.getAuthState();
  });
  const activeProfile = useSelector(selectActiveProfile);
  const dispatch = useDispatch();

  const isPublicRoute = () => {
    const currentPathname = window.location?.pathname || '';
    return publicRoutes.some((path) => currentPathname.includes(path));
  };

  useEffect(() => {
    if (!oktaAuth) {
      return;
    }

    // Update Security provider with latest authState
    const currentAuthState = oktaAuth.authStateManager.getAuthState();
    if (currentAuthState !== authState) {
      setAuthState(currentAuthState);
    }
    if (oktaAuth.tokenManager && oktaAuth.tokenManager.on) {
      // Subscribe to tokenManager events

      oktaAuth.tokenManager.on('error', (err) => {
        console.error('Error during token renewal:', err);
        // Clear all sessionStorage data
        sessionStorage.clear();
        // Clear all cookies
        clearAllCookies();
        if (!isOpenNotificationTokenIsClearDialog) {
          setIsOpenNotificationTokenIsClearDialog(true);
        }
      });
    }
  }, [oktaAuth, authState]);

  const { cancel: cancelRefreshToken } = useRequest(
    async () => {
      if (oktaAuth) {
        const auth = await oktaAuth.authStateManager.getAuthState();
        if (auth?.isAuthenticated) {
          const renewToken = await oktaAuth?.token?.renewTokens();
          return renewToken;
        }
      }
    },
    {
      pollingInterval: 3000000,
      pollingErrorRetryCount: 3,
      onSuccess: async (auth) => {
        if (auth?.accessToken) {
          oktaAuth.tokenManager.setTokens(auth);
          oktaAuth.authStateManager.updateAuthState();
        }
      },
    },
  );

  useEffect(() => {
    if (!oktaAuth) {
      return;
    }
    const handler = async (auth) => {
      if (!auth.isAuthenticated) {
        setAuthState(null);
        dispatch(logoutAction());
        return;
      }
      dispatch(setTokenLogin(auth.accessToken.accessToken));
      setAuthState(auth);
    };

    oktaAuth.authStateManager.subscribe(handler);
    // Trigger an initial change event to make sure authState is latest
    oktaAuth.start();
    return () => {
      oktaAuth.authStateManager.unsubscribe(handler);
    };
  }, [dispatch, oktaAuth]);

  const handleLogout = useCallback(async () => {
    cancelRefreshToken();
    await oktaAuth.signOut();
  }, [oktaAuth]);

  const handleLogin = useCallback(
    (tokens) => {
      oktaAuth.tokenManager.setTokens(tokens);
      oktaAuth.authStateManager.updateAuthState();
      oktaAuth.handleRedirect();
    },
    [oktaAuth],
  );

  const onCloseNotificationTokenIsClearDialog = (action) => {
    setIsOpenNotificationTokenIsClearDialog(false);
    if (action === 'agree') {
      onClickLogin();
    }
  };

  const onClickLogin = async () => {
    await oktaAuth.signInWithRedirect(`${window.location.origin + BASE_URL}`);
  };

  const oktaContext = useMemo(
    () => ({ oktaAuth, authState, handleLogout, handleLogin }),
    [authState, handleLogin, handleLogout, oktaAuth],
  );

  return (
    <OktaContext.Provider value={oktaContext}>
      <>{children}</>
      <ConfirmationModal
        open={isOpenNotificationTokenIsClearDialog}
        title={'Phiên đăng nhập đã hết hạn'}
        content={
          'Phiên đăng nhập của Anh/Chị đã hết hạn, vui lòng đăng nhập lại để tiếp tục sử dụng hệ thống.'
        }
        cancelButtonTitle={'Đóng'}
        agreeButtonTitle={'Đăng nhập lại'}
        onClose={onCloseNotificationTokenIsClearDialog}
      />
    </OktaContext.Provider>
  );
};
export default Security;

Security.propTypes = {
  oktaAuth: PropTypes.any,
  children: PropTypes.any,
};
